<template>
  <component
    :is="as"
    :type="type"
    :class="[classes, textSizeClass]"
    :disabled="disabled"
    v-bind="$attrs"
    role="button"
    v-on="$listeners"
  >
    <slot />
  </component>
</template>

<script>
import classes, { getModifierClass } from '~~/common/vue/mixins/classes';

export const sizes = ['s', 'm', 'l'];
export const textSizes = ['s', 'm', 'l'];
export const tags = ['button', 'a', 'nuxt-link'];
export const variants = [
  'bare',
  'solid-light',
  'solid-primary',
  'solid-dark',
  'outline-light',
  'outline-primary',
  'outline-dark',
];

export default {
  name: 'EButton',
  mixins: [classes],
  props: {
    /**
     * `button, a, nuxt-link`
     */
    as: {
      type: String,
      default: 'button',
      validator: (value) => tags.includes(value),
    },
    type: {
      type: String,
      default: 'button',
    },
    disabled: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    /**
     * `bare, solid-light, solid-dark, solid-primary, outline-light, outline-dark,
     * outline-primary
     */
    variant: {
      type: String,
      default: 'solid-light',
      validator: (value) => variants.includes(value),
      cssClass: true,
    },
    /**
     * Enables round sides (as opposed to default rounded corners)
     */
    rounded: {
      type: Boolean,
      default: false,
      cssClass: true,
    },
    /**
     * `s, m, l` The size of the button
     */
    size: {
      type: String,
      default: 'l',
      validator: (value) => sizes.includes(value),
      cssClass: true,
    },
    /**
     * `s, m, l` The size of the text (default: same as `size`)
     */
    textSize: {
      type: [String, null],
      validator: (value) => textSizes.includes(value),
      default: null,
    },
  },
  computed: {
    textSizeClass() {
      const realSize = this.textSize || this.size;
      return getModifierClass(this.baseClass, `text-size-${realSize}`);
    },
  },
};
</script>

<style lang="scss" scoped>
$sizes: (
  s: (
    height: $space-2l,
    padding: $space-s - $border-width,
    padding-rounded: $space-m - $border-width,
  ),
  m: (
    height: $space-3l,
    padding: $space-m - $border-width,
    padding-rounded: $space-l - $border-width,
  ),
  l: (
    height: $space-4l,
    padding: $space-l - $border-width,
    padding-rounded: $space-1l - $border-width,
  )
);

$font-sizes: (
  s: $font-1s,
  m: $font-m,
  l: $font-l,
);

$variants: (
  bare: (
    color: $color-text-secondary,
    active-color: $color-text-primary,
  ),
  solid-light: (
    color: $color-text-primary,
    background-color: $white,
    border-color: $gray-20,
    active-background-color: $gray-10,
    active-border-color: $gray-10,
  ),
  solid-primary: (
    color: $color-text-tertiary,
    background-color: $blue-50,
    border-color: $blue-50,
    active-background-color: $blue-60,
    active-border-color: $blue-60,
  ),
  solid-dark: (
    color: $gray-20,
    background-color: $gray-60,
    border-color: $gray-60,
    active-color: $white,
    active-background-color: $gray-50,
    active-border-color: $gray-50,
  ),
  outline-light: (
    color: $color-text-tertiary,
    background-color: none,
    border-color: $white,
    active-color: $color-text-primary,
    active-background-color: $white,
  ),
  outline-primary: (
    color: $blue-50,
    background-color: none,
    border-color: $blue-50,
    active-color: $white,
    active-background-color: $blue-60,
  ),
  outline-dark: (
    color: $color-text-primary,
    background-color: none,
    border-color: $gray-60,
    active-color: $white,
    active-background-color: $gray-70,
  ),
);

.button {
  -webkit-tap-highlight-color: transparent;
  position: relative;
  display: inline-flex;
  background-color: transparent;
  font-family: $family;
  white-space: nowrap;
  text-align: center;
  justify-content: center;
  cursor: pointer;
  border: none;
  border-radius: $border-radius-s;
  padding: 0;
  appearance: none;
  align-items: center;
  letter-spacing: 0.1px;
  transition: all $timing-2s $easing;

  &,
  &:hover,
  &:focus {
    text-decoration: none;
  }

  &:focus {
    @extend %keyboard-nav-focus;
  }

  @each $name, $map in $sizes {
    &--size-#{$name} {
      padding-left: map-get($map, padding);
      padding-right: map-get($map, padding);
      height: map-get($map, height);

      &.button--rounded {
        padding-left: map-get($map, padding-rounded);
        padding-right: map-get($map, padding-rounded);
      }
    }
  }

  @each $size, $fs in $font-sizes {
    &--text-size-#{$size} {
      font-size: $fs;
    }
  }

  @each $name, $map in $variants {
    $color: map-get($map, color);
    $background-color: map-get($map, background-color);
    $border-color: map-get($map, border-color);
    $active-color: map-get($map, active-color);
    $active-background-color: map-get($map, active-background-color);
    $active-border-color: map-get($map, active-border-color);

    &--variant-#{$name} {
      color: $color;
      background-color: $background-color;

      @if $border-color {
        border: $border-width solid $border-color;
      }

      &:active,
      &:hover,
      &:focus {
        color: $active-color;
        background-color: $active-background-color;

        @if $active-border-color {
          border-color: $active-border-color;
        }
      }
    }
  }

  &--disabled {
    opacity: $opacity-m;
    pointer-events: none;
  }

  &--rounded {
    border-radius: $border-radius-full;
  }
}
</style>

<docs>
Default:

```js
<div>
<EButton>Button</EButton>
<EButton as="a">Anchor</EButton>
</div>
```

Bare:

```js
<EButton variant="bare">Bare</EButton>
```

Solid variants:

```js
<div style="background-color: #00c8ab; padding: 10px;">
<EButton>Light</EButton>
<EButton variant="solid-primary">Primary</EButton>
<EButton variant="solid-dark">Dark</EButton>
</div>
```

Outline variants:

```js
<div style="background-color: #00c8ab; padding: 10px; margin-top: 10px;">
<EButton variant="outline-light">Light</EButton>
<EButton variant="outline-primary">Primary</EButton>
<EButton variant="outline-dark">Dark</EButton>
</div>
```

Sizing:

```js
<EButton size="s">Small</EButton>
<EButton size="m">Medium</EButton>
<EButton size="l">Large</EButton>
<EButton size="s" text-size="m">Small + Medium text</EButton>
<EButton size="m" text-size="s">Medium + Small text</EButton>
```

Rounded:

```js
<EButton rounded>Default</EButton>
<EButton rounded variant="solid-dark">Dark</EButton>
<EButton rounded size="s">Size S</EButton>
<EButton rounded size="s" variant="solid-dark">Size S Dark</EButton>
```

Disabled:

```js
<EButton disabled>Disabled</EButton>
```

Submit:

```js
<form onsubmit="console.log('submitted'); return false;">
<EButton type="submit">Submit</EButton>
</form>
```
</docs>
